// The immutable protectedData.vat record (dynamic VAT spec §2, customer
// service fee spec §2: version 2) plus the receipt keys the email templates
// read. Written once by the server; never rewritten. CommonJS: shared by
// server and client (display helpers). No object spread here, see
// transaction.js stripCase.
const { LINE_ITEM, lineTotal } = require('./lineItems');
const { PATH } = require('./transaction');
const { rateLabel } = require('./notes');

const SNAPSHOT_VERSION = 2;
const FEE_MODEL_CUSTOMER = 'customer';

// Receipt keys keep CheckoutPage's unit: euros as a plain number
// (100.00 € → 100), because the email templates print `.amount` as-is.
const plainMoney = (cents, currency) => ({ amount: Math.round(cents) / 100, currency });

const supplyBlock = (supply, net, vat) =>
  supply
    ? {
        case: supply.case,
        rate: supply.rate,
        label: supply.label,
        noteKey: supply.noteKey,
        placeOfSupply: supply.placeOfSupply,
        net,
        vat,
        gross: net + vat,
      }
    : undefined;

const amountOf = li => (li.lineTotal ? li.lineTotal.amount : lineTotal(li).amount);

const totalOf = (lineItems, code) => {
  const li = lineItems.find(item => item.code === code && !item.reversal);
  return li ? amountOf(li) : 0;
};

// The receipt keys from line items — either ours (no lineTotal yet) or the
// API's (lineTotal present). Euros, like CheckoutPage always wrote them.
// payInWithoutVat / payInVat are the work line; a layout with a
// customer-commission line also gets the fee keys and the customer totals.
// The payout keys split what the provider receives: payOutVat is the
// provider's own sale VAT, payOutWithoutVat the rest of the provider-side
// lines, so neither goes negative on a reverse-charged sale (R36).
const legacyKeysFromLineItems = (lineItems, path) => {
  const night = lineItems.find(li => li.code === LINE_ITEM.NIGHT && !li.reversal);
  const currency = night.unitPrice.currency;
  const net = night.unitPrice.amount;
  const saleTax = totalOf(lineItems, LINE_ITEM.SALE_TAX);
  const providerSaleTax = totalOf(lineItems, LINE_ITEM.PROVIDER_SALE_TAX);
  const payOutVat = path === PATH.STRIPE || path === PATH.LIGHT_CARD ? saleTax : providerSaleTax;
  const providerTotal = lineItems
    .filter(li => !li.reversal && li.includeFor && li.includeFor.includes('provider'))
    .reduce((sum, li) => sum + amountOf(li), 0);
  const keys = {
    payInWithoutVat: plainMoney(net, currency),
    payInVat: plainMoney(saleTax, currency),
    payOutWithoutVat: plainMoney(providerTotal - payOutVat, currency),
    payOutVat: plainMoney(payOutVat, currency),
  };
  const feeLine = lineItems.find(li => li.code === LINE_ITEM.CUSTOMER_COMMISSION && !li.reversal);
  if (!feeLine) return keys;
  const fee = amountOf(feeLine);
  const feeVat = totalOf(lineItems, LINE_ITEM.CUSTOMER_COMMISSION_VAT);
  return Object.assign(keys, {
    payInFee: plainMoney(fee, currency),
    payInFeeVat: plainMoney(feeVat, currency),
    payInWithoutVatTotal: plainMoney(net + fee, currency),
    payInVatTotal: plainMoney(saleTax + feeVat, currency),
  });
};

const buildSnapshot = ({ path, resolution, lineItems, seller, buyer, now = new Date() }) => {
  if (resolution && resolution.path && resolution.path !== path) {
    throw new Error(`VAT path mismatch: ${path} vs ${resolution.path}`);
  }
  const find = code => lineItems.find(li => li.code === code);
  const total = code => {
    const li = find(code);
    return li ? lineTotal(li).amount : 0;
  };
  const night = find(LINE_ITEM.NIGHT);
  const net = night.unitPrice.amount;
  const saleTax = total(LINE_ITEM.SALE_TAX);
  const providerSaleTax = total(LINE_ITEM.PROVIDER_SALE_TAX);
  const fee = total(LINE_ITEM.CUSTOMER_COMMISSION);
  const feeVat = total(LINE_ITEM.CUSTOMER_COMMISSION_VAT);
  const { supplies } = resolution;

  const vat = {
    v: SNAPSHOT_VERSION,
    path,
    resolvedAt: now.toISOString(),
    feeModel: FEE_MODEL_CUSTOMER,
    customerPrice: net + fee,
    supplies: {},
    seller: seller || null,
    buyer: buyer || null,
  };
  if (path === PATH.STRIPE) {
    vat.supplies.core = supplyBlock(supplies.core, net, saleTax);
  } else if (path === PATH.INVOICE || path === PATH.LIGHT_MAIN) {
    vat.supplies.customerSale = supplyBlock(supplies.customerSale, net, saleTax);
    vat.supplies.providerSale = supplyBlock(supplies.providerSale, net, providerSaleTax);
  } else if (path === PATH.LIGHT_CARD) {
    vat.supplies.customerSale = supplyBlock(supplies.customerSale, net, saleTax);
  } else {
    throw new Error(`Unknown VAT path: ${path}`);
  }
  vat.supplies.serviceFee = supplyBlock(supplies.serviceFee, fee, feeVat);

  // Notion 6.1: an Åland seller selling abroad shows the Business ID, never
  // the FI VAT number.
  const cases = Object.keys(vat.supplies).map(k => vat.supplies[k] && vat.supplies[k].case);
  if (vat.seller && cases.includes('AX-2')) {
    vat.seller = Object.assign({}, vat.seller, { vatNumber: null });
  }

  // English rate labels for the receipt templates: the customer side is the
  // core supply on Stripe-direct or the customer sale on the invoice paths;
  // the fee is the service fee; the provider side is the core supply on
  // Stripe-direct or the provider sale on the invoice paths. Null when that
  // side has no supply (light-card has no providerSale).
  const customerSupply = vat.supplies.core || vat.supplies.customerSale || null;
  const providerSupply = vat.supplies.core || vat.supplies.providerSale || null;
  const feeSupply = vat.supplies.serviceFee || null;

  return Object.assign({ vat }, legacyKeysFromLineItems(lineItems, path), {
    vatCustomerRateLabel: customerSupply ? rateLabel(customerSupply, 'en') : null,
    vatFeeRateLabel: feeSupply ? rateLabel(feeSupply, 'en') : null,
    vatProviderRateLabel: providerSupply ? rateLabel(providerSupply, 'en') : null,
  });
};

module.exports = { buildSnapshot, legacyKeysFromLineItems };
